import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useKeydown from "@hooks/useKeydown";
import { formatDate } from "@utils/util";
import "../styles/Calendar.scss";

export default function Calendar({
  isVisible,
  onClose,
  onDateSelect,
  isActive,
  currentChannel,
}) {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("date");
  const [dates, setDates] = useState([]);
  const [times, setTimes] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedMinute, setSelectedMinute] = useState("");

  const listRefs = {
    date: useRef(null),
    time: useRef(null),
    minute: useRef(null),
  };

  const updateSelection = (section, value, smooth = true) => {
    const listRef = listRefs[section].current;
    if (!listRef) return;

    const fontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const containerHeight = listRef.parentElement.clientHeight / fontSize;
    const itemHeight = 5;
    const visibleItems = 3;
    const visibleHeight = itemHeight * visibleItems;
    const centerPosition = (visibleHeight - itemHeight) / 2;

    const initialOffset = itemHeight / 2;

    const offset = value * itemHeight + initialOffset;

    listRef.style.transition = smooth ? "transform 0.2s ease" : "none";
    listRef.style.transform = `translateY(-${offset}rem)`;

    if (section === "date") setSelectedDate(dates[value]);
    else if (section === "time") setSelectedTime(times[value]);
    else setSelectedMinute(minutes[value]);
  };

  useEffect(() => {
    if (currentChannel?.archived_channel?.archiveDays) {
      const archiveDays = currentChannel.archived_channel.archiveDays;
      const now = new Date();

      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const roundedMinute = Math.floor(currentMinute / 5) * 5;
      const minuteIndex = roundedMinute / 5;

      const availableDates = Array.from({ length: archiveDays }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        return {
          display: formatDate(date, "MMMM dd, yyyy"),
          value: date,
        };
      });

      const availableTimes = Array.from({ length: 24 }, (_, i) => {
        const hour = i.toString().padStart(2, "0");
        return {
          display: `${hour}`,
          value: hour,
        };
      });

      const availableMinutes = Array.from({ length: 12 }, (_, i) => {
        const minute = (i * 5).toString().padStart(2, "0");
        return {
          display: minute,
          value: minute,
        };
      });

      updateSelection("date", 0, false);
      updateSelection("time", currentHour, false);
      updateSelection("minute", minuteIndex, false);

      setDates(availableDates);
      setTimes(availableTimes);
      setMinutes(availableMinutes);

      setSelectedDate(availableDates[0]);
      setSelectedTime(availableTimes[currentHour]);
      setSelectedMinute(availableMinutes[minuteIndex]);
    }
  }, [currentChannel]);

  useEffect(() => {
    if (isVisible && isActive) {
      const currentList =
        activeSection === "date"
          ? dates
          : activeSection === "time"
            ? times
            : minutes;
      const currentSelected =
        activeSection === "date"
          ? selectedDate
          : activeSection === "time"
            ? selectedTime
            : selectedMinute;

      const currentIndex = currentList.indexOf(currentSelected);
      updateSelection(activeSection, currentIndex || 0, false);
    }
  }, [activeSection, isVisible, isActive]);

  useKeydown({
    isActive: isVisible && isActive,

    left: () => {
      if (activeSection === "minute") {
        setActiveSection("time");
        if (!selectedTime) {
          setSelectedTime(times[0]);
          updateSelection("time", 0, false);
        }
      } else if (activeSection === "time") {
        setActiveSection("date");
        if (!selectedDate) {
          setSelectedDate(dates[0]);
          updateSelection("date", 0, false);
        }
      }
    },

    right: () => {
      if (activeSection === "date") {
        setActiveSection("time");
        if (!selectedTime) {
          setSelectedTime(times[0]);
          updateSelection("time", 0, false);
        }
      } else if (activeSection === "time") {
        setActiveSection("minute");
        if (!selectedMinute) {
          setSelectedMinute(minutes[0]);
          updateSelection("minute", 0, false);
        }
      }
    },

    up: () => {
      const currentList =
        activeSection === "date"
          ? dates
          : activeSection === "time"
            ? times
            : minutes;
      const currentSelected =
        activeSection === "date"
          ? selectedDate
          : activeSection === "time"
            ? selectedTime
            : selectedMinute;
      const currentIndex = currentList.indexOf(currentSelected);
      if (currentIndex > 0) {
        updateSelection(activeSection, currentIndex - 1);
      }
    },

    down: () => {
      const currentList =
        activeSection === "date"
          ? dates
          : activeSection === "time"
            ? times
            : minutes;
      const currentSelected =
        activeSection === "date"
          ? selectedDate
          : activeSection === "time"
            ? selectedTime
            : selectedMinute;
      const currentIndex = currentList.indexOf(currentSelected);
      if (currentIndex < currentList.length - 1) {
        updateSelection(activeSection, currentIndex + 1);
      }
    },

    ok: () => {
      if (selectedDate && selectedTime && selectedMinute) {
        const selectedDateTime = new Date(selectedDate.value);
        selectedDateTime.setHours(parseInt(selectedTime.value));
        selectedDateTime.setMinutes(parseInt(selectedMinute.value));
        onDateSelect(Math.floor(selectedDateTime.getTime() / 1000));
      }
    },

    back: onClose,
  });

  return (
    <div className={`calendar-overlay ${isVisible ? "visible" : ""}`}>
      <div className="calendar-container">
        <div
          className={`calendar-section ${activeSection === "date" ? "active" : ""}`}
        >
          <h3>{t("Date")}</h3>
          <div className="date-list" ref={listRefs.date}>
            {dates.map((date, index) => (
              <div
                key={date.display}
                className={`date-item ${selectedDate == date ? "active" : ""}`}
              >
                {date.display}
              </div>
            ))}
          </div>
        </div>

        <div
          className={`calendar-section ${activeSection === "time" ? "active" : ""}`}
        >
          <h3>{t("Time")}</h3>
          <div className="time-list" ref={listRefs.time}>
            {times.map((time, index) => (
              <div
                key={time.display}
                className={`time-item ${selectedTime === time ? "active" : ""}`}
              >
                {time.display}
              </div>
            ))}
          </div>
        </div>

        <div
          className={`calendar-section ${activeSection === "minute" ? "active" : ""}`}
        >
          <h3>{t("Minute")}</h3>
          <div className="minute-list" ref={listRefs.minute}>
            {minutes.map((minute, index) => (
              <div
                key={minute.display}
                className={`minute-item ${selectedMinute === minute ? "active" : ""}`}
              >
                {minute.display}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
